import React, { Suspense } from 'react';
import Link from 'next/link';
import { Filter } from 'lucide-react';
import { SearchResult, SearchItem } from './types';

type AwaitableSearchParams = Promise<Record<string, unknown>> | Record<string, unknown> | undefined;

type Primitive = string | number | boolean | null | undefined;

interface FilterValue {
  ID?: string | number;
  VALUE?: string | number;
  NAME?: string;
  CODE?: string;
  XML_ID?: string;
  PRODUCT_IDS?: string | string[] | number[] | null;
  [key: string]: unknown;
}

interface FilterDefinition {
  CODE: string;
  NAME?: string;
  VALUES?: FilterValue[];
  [key: string]: unknown;
}

interface FilterResponse {
  filters?: FilterDefinition[];
  [key: string]: unknown;
}

interface SearchFiltersData {
  filters: FilterDefinition[];
}

interface SearchPageComponents {
  Pagination: React.ComponentType<{ totalPages: number } & Record<string, unknown>>;
  FilterMob: React.ComponentType<{
    section: SearchFiltersData;
    selectedCount: number;
    triggerClassName?: string;
  } & Record<string, unknown>>;
  FilterItems: React.ComponentType<{
    section: SearchFiltersData;
  } & Record<string, unknown>>;
  SortMenu: React.ComponentType<Record<string, unknown>>;
  ProductCard: React.ComponentType<{
    item: unknown;
    showPrices?: boolean;
    showDiscount?: boolean;
  } & Record<string, unknown>>;
  SearchSkeleton: React.ComponentType<Record<string, unknown>>;
}

export interface SearchPageConfig {
  fetchSearchItems: (category: string, query: string) => Promise<SearchResult>;
  fetchFilterData: (section: string) => Promise<FilterResponse>;
  sectionCode?: string;
  basePath?: string;
  pageSize?: number;
  components: SearchPageComponents;
}

interface SearchResultsProps {
  searchParams: AwaitableSearchParams;
}

interface ActiveFilterMeta {
  key: string;
  value: string;
  label: string;
  href: string;
}

interface NormalizeParamsResult {
  paramsMap: Record<string, string | string[]>;
  queryValue: string | null;
}

const DEFAULT_SECTION = 'catalog';
const DEFAULT_BASE_PATH = '/search';
const DEFAULT_PAGE_SIZE = 24;

const OBJECT_STRING_CANDIDATE_KEYS = [
  'value', 'VALUE',
  'display', 'DISPLAY',
  'name', 'NAME',
  'label', 'LABEL',
  'title', 'TITLE',
  'text', 'TEXT',
  'code', 'CODE',
  'slug', 'SLUG',
  'xml_id', 'XML_ID',
  'alias', 'ALIAS'
];

const PRODUCT_ID_CANDIDATES = [
  'id', 'ID',
  'product_id', 'productId', 'PRODUCT_ID',
  'PARENT_ID', 'parent_id', 'parentId',
  'element_id', 'ELEMENT_ID',
  'iblock_element_id', 'IBLOCK_ELEMENT_ID',
  'offer_id', 'offerId', 'OFFER_ID',
  'sku_id', 'SKU_ID',
  'code', 'CODE',
  'slug', 'SLUG',
];

function normalizePrimitive(value: Primitive): string {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).trim().toLowerCase();
}

function collectStrings(source: unknown, target: Set<string>) {
  if (!target) {
    return;
  }

  if (source === null || source === undefined) {
    return;
  }

  if (typeof source === 'string' || typeof source === 'number' || typeof source === 'boolean') {
    const normalized = normalizePrimitive(source);
    if (normalized) {
      target.add(normalized);
    }
    return;
  }

  if (Array.isArray(source)) {
    source.forEach((entry) => collectStrings(entry, target));
    return;
  }

  if (typeof source === 'object') {
    OBJECT_STRING_CANDIDATE_KEYS.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        collectStrings((source as Record<string, unknown>)[key], target);
      }
    });
  }
}

function normalizeProductIds(payload: unknown): string[] {
  if (!payload) {
    return [];
  }

  const source = Array.isArray(payload) ? payload : [payload];
  const result: string[] = [];

  source.forEach((entry) => {
    if (entry === null || entry === undefined) {
      return;
    }
    String(entry)
      .split(/[,;|\s]+/)
      .map((token) => token.trim())
      .filter(Boolean)
      .forEach((token) => result.push(token));
  });

  return result;
}

function collectProductIds(item: unknown): string[] {
  if (!item || typeof item !== 'object') {
    return [];
  }

  const result = new Set<string>();
  const source = item as Record<string, unknown>;

  PRODUCT_ID_CANDIDATES.forEach((key) => {
    const value = source[key];
    if (typeof value === 'string' || typeof value === 'number') {
      result.add(String(value));
    }
  });

  if (typeof source.product === 'object' && source.product) {
    PRODUCT_ID_CANDIDATES.forEach((key) => {
      const value = (source.product as Record<string, unknown>)[key];
      if (typeof value === 'string' || typeof value === 'number') {
        result.add(String(value));
      }
    });
  }

  if (typeof source.offer === 'object' && source.offer) {
    PRODUCT_ID_CANDIDATES.forEach((key) => {
      const value = (source.offer as Record<string, unknown>)[key];
      if (typeof value === 'string' || typeof value === 'number') {
        result.add(String(value));
      }
    });
  }

  return Array.from(result);
}

function collectItemValuesByCode(item: unknown, code: string): Set<string> {
  const result = new Set<string>();
  if (!code) {
    return result;
  }

  const variations = new Set([code, code.toLowerCase(), code.toUpperCase()]);
  const source = typeof item === 'object' && item ? item as Record<string, unknown> : {};

  const candidateSources: unknown[] = [];

  variations.forEach((variant) => {
    if (variant in source) {
      candidateSources.push(source[variant]);
    }

    if (typeof source.properties === 'object' && source.properties && variant in (source.properties as Record<string, unknown>)) {
      candidateSources.push((source.properties as Record<string, unknown>)[variant]);
    }

    if (typeof source.PROPERTIES === 'object' && source.PROPERTIES && variant in (source.PROPERTIES as Record<string, unknown>)) {
      candidateSources.push((source.PROPERTIES as Record<string, unknown>)[variant]);
    }

    if (typeof source.PROPERTY === 'object' && source.PROPERTY && variant in (source.PROPERTY as Record<string, unknown>)) {
      candidateSources.push((source.PROPERTY as Record<string, unknown>)[variant]);
    }

    if (typeof source.props === 'object' && source.props && variant in (source.props as Record<string, unknown>)) {
      candidateSources.push((source.props as Record<string, unknown>)[variant]);
    }

    const rootSections = ['attributes', 'facets', 'filters', 'meta', 'taxonomy', 'system', 'languages'] as const;
    rootSections.forEach((section) => {
      const sectionValue = (source as Record<string, unknown>)[section];
      if (section === 'languages') {
        // languages — объект объектов: { ru: {...}, en: {...} }
        if (sectionValue && typeof sectionValue === 'object' && !Array.isArray(sectionValue)) {
          Object.values(sectionValue as Record<string, unknown>).forEach((langEntry) => {
            if (langEntry && typeof langEntry === 'object' && !Array.isArray(langEntry)) {
              if (variant in (langEntry as Record<string, unknown>)) {
                candidateSources.push((langEntry as Record<string, unknown>)[variant]);
              }
            }
          });
        }
        return;
      }

      if (sectionValue && typeof sectionValue === 'object' && !Array.isArray(sectionValue)) {
        const record = sectionValue as Record<string, unknown>;
        if (variant in record) {
          candidateSources.push(record[variant]);
        }
      }
    });
  });

  candidateSources.forEach((entry) => collectStrings(entry, result));
  return result;
}

function hasIntersection(setA: Set<string>, setB: Set<string> | Set<unknown>): boolean {
  if (!setA || !setB || setA.size === 0 || setB.size === 0) {
    return false;
  }
  for (const value of setA) {
    if ((setB as Set<string>).has(value)) {
      return true;
    }
  }
  return false;
}

function normalizeSearchParams(input: Record<string, unknown> | null | undefined): NormalizeParamsResult {
  const paramsEntries = Object.entries(input ?? {});
  const paramsMap: Record<string, string | string[]> = {};

  paramsEntries.forEach(([key, value]) => {
    if (paramsMap[key] === undefined) {
      paramsMap[key] = Array.isArray(value) ? value.map(String) : String(value);
    } else if (Array.isArray(paramsMap[key])) {
      (paramsMap[key] as string[]).push(String(value));
    } else {
      paramsMap[key] = [paramsMap[key] as string, String(value)];
    }
  });

  const rawQueryValue = paramsMap.query ?? paramsMap.q;
  const queryValue = Array.isArray(rawQueryValue) ? rawQueryValue[0] : rawQueryValue;
  const normalizedQuery = typeof queryValue === 'string' && queryValue.trim() ? queryValue.trim() : null;

  return { paramsMap, queryValue: normalizedQuery };
}

function buildQueryString(params: Record<string, string | string[]>): string {
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }
    if (Array.isArray(value)) {
      if (value.length > 0) {
        queryParams.append(key, value.join(','));
      }
    } else if (value !== '') {
      queryParams.append(key, value);
    }
  });

  return queryParams.toString();
}

function buildFiltersData(
  rawFiltersData: FilterResponse,
  itemsForFilters: unknown[]
): SearchFiltersData {
  const filtersArray = Array.isArray(rawFiltersData?.filters) ? rawFiltersData.filters : [];
  const resultIdSet = new Set(itemsForFilters.flatMap((item) => collectProductIds(item)).filter(Boolean));

  if (resultIdSet.size === 0) {
    return {
      filters: filtersArray,
    };
  }

  const filteredFilters = filtersArray
    .map((filter) => {
      const code = filter?.CODE ?? '';
      if (!code) {
        return null;
      }

      const values = Array.isArray(filter?.VALUES) ? filter.VALUES : [];
      if (values.length === 0) {
        return null;
      }

      const matchers = values.map((value) => {
        const normalizedCandidates = new Set<string>();
        collectStrings(value, normalizedCandidates);
        const fallbackProductIds = new Set(
          normalizeProductIds(value?.PRODUCT_IDS).map((id) => String(id))
        );

        return {
          value,
          normalizedCandidates,
          fallbackProductIds,
          matchedProductIds: new Set<string>(),
        };
      });

      itemsForFilters.forEach((item) => {
        const itemValues = collectItemValuesByCode(item, code);
        const itemIds = collectProductIds(item).map((id) => String(id));

        matchers.forEach((matcher) => {
          let matched = false;

          if (matcher.normalizedCandidates.size > 0 && itemValues.size > 0) {
            matched = hasIntersection(itemValues, matcher.normalizedCandidates);
          }

          if (!matched && matcher.fallbackProductIds.size > 0) {
            matched = itemIds.some((id) => matcher.fallbackProductIds.has(id));
          }

          if (matched) {
            itemIds.forEach((id) => matcher.matchedProductIds.add(id));
          }
        });
      });

      const filteredValues = matchers
        .map(({ value, matchedProductIds }) => {
          if (matchedProductIds.size === 0) {
            return null;
          }
          return {
            ...value,
            PRODUCT_IDS: Array.from(matchedProductIds),
          };
        })
        .filter(Boolean) as FilterValue[];

      if (filteredValues.length === 0) {
        return null;
      }

      return {
        ...filter,
        VALUES: filteredValues,
      };
    })
    .filter(Boolean) as FilterDefinition[];

  if (filteredFilters.length === 0) {
    return {
      filters: filtersArray,
    };
  }

  return {
    filters: filteredFilters,
  };
}

function buildActiveFilters(
  paramsMap: Record<string, string | string[]>,
  basePath: string,
  filtersData: SearchFiltersData,
  rawFiltersData: FilterResponse
): ActiveFilterMeta[] {
  const activeFilters: ActiveFilterMeta[] = [];
  const filterMetaMap = new Map<string, FilterDefinition>();

  filtersData.filters.forEach((filter) => filterMetaMap.set(filter.CODE, filter));
  (rawFiltersData.filters ?? []).forEach((filter) => {
    if (!filterMetaMap.has(filter.CODE)) {
      filterMetaMap.set(filter.CODE, filter);
    }
  });

  const currentParams = new URLSearchParams();

  Object.entries(paramsMap).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }
    if (Array.isArray(value)) {
      if (value.length > 0) {
        currentParams.set(key, value.join(','));
      }
    } else if (value !== '') {
      currentParams.set(key, value);
    }
  });

  const persistentParams = new URLSearchParams();
  const rawQueryValue = paramsMap.query ?? paramsMap.q;
  const queryValue = Array.isArray(rawQueryValue) ? rawQueryValue[0] : rawQueryValue;
  if (typeof queryValue === 'string' && queryValue.trim()) {
    persistentParams.set('query', queryValue.trim());
  }

  const seenFilters = new Set<string>();

  Array.from(currentParams.entries()).forEach(([key, value]) => {
    if (key === 'page' || key === 'sort' || key === 'query' || key === 'q') {
      return;
    }

    const rawValues = String(value)
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);

    if (rawValues.length === 0) {
      return;
    }

    const filterMeta = filterMetaMap.get(key);

    rawValues.forEach((rawVal) => {
      const dedupeKey = `${key}:${rawVal}`;
      if (seenFilters.has(dedupeKey)) {
        return;
      }
      seenFilters.add(dedupeKey);

      const optionSource = Array.isArray(filterMeta?.VALUES) ? filterMeta?.VALUES : [];
      const optionMeta = optionSource.find((opt) => String(opt?.ID ?? opt?.VALUE) === rawVal);
      const optionLabel = optionMeta?.VALUE ?? rawVal;
      const filterLabel = filterMeta?.NAME ?? key;

      const removeParams = new URLSearchParams(currentParams.toString());
      removeParams.delete('page');

      const existing = removeParams.get(key);
      if (existing) {
        const remaining = existing
          .split(',')
          .map((v) => v.trim())
          .filter((v) => v && v !== rawVal);

        if (remaining.length > 0) {
          removeParams.set(key, remaining.join(','));
        } else {
          removeParams.delete(key);
        }
      }

      const removeQuery = removeParams.toString();
      const href = removeQuery ? `${basePath}?${removeQuery}` : basePath;

      activeFilters.push({
        key,
        value: rawVal,
        label: `${filterLabel}: ${optionLabel}`,
        href,
      });
    });
  });

  const persistentQuery = persistentParams.toString();
  const resetFiltersHref = persistentQuery ? `${basePath}?${persistentQuery}` : basePath;

  if (activeFilters.length > 0) {
    activeFilters.push({
      key: '__reset__',
      value: '__reset__',
      label: '',
      href: resetFiltersHref,
    });
  }

  return activeFilters;
}

function extractSearchParams(searchParams: AwaitableSearchParams): Promise<Record<string, unknown> | null> {
  if (!searchParams) {
    return Promise.resolve(null);
  }
  if (typeof (searchParams as Promise<unknown>).then === 'function') {
    return (searchParams as Promise<Record<string, unknown>>).catch(() => null);
  }
  return Promise.resolve(searchParams as Record<string, unknown>);
}

export function createSearchPage({
  fetchSearchItems,
  fetchFilterData,
  sectionCode = DEFAULT_SECTION,
  basePath = DEFAULT_BASE_PATH,
  pageSize = DEFAULT_PAGE_SIZE,
  components,
}: SearchPageConfig) {
  const {
    Pagination,
    FilterMob,
    FilterItems,
    SortMenu,
    ProductCard,
    SearchSkeleton,
  } = components;

  async function SearchResultsContent({ searchParams }: SearchResultsProps) {
    const resolvedParams = await extractSearchParams(searchParams);
    const { paramsMap, queryValue } = normalizeSearchParams(resolvedParams);
    const searchQuery = resolvedParams?.q ?? resolvedParams?.query ?? queryValue;

    if (!resolvedParams || !searchQuery) {
      return (
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">Пожалуйста, введите поисковый запрос</p>
          <Link href="/shop">
            <button className="px-4 py-2 bg-blue-500 text-white rounded">Перейти в каталог</button>
          </Link>
        </div>
      );
    }

    const queryString = buildQueryString(paramsMap);
    const baseQueryParams = new URLSearchParams();
    if (queryValue) {
      baseQueryParams.set('query', queryValue);
    }

    const [allItemsShop, baseItemsShop, rawFiltersData] = await Promise.all([
      fetchSearchItems(sectionCode, queryString),
      fetchSearchItems(sectionCode, baseQueryParams.toString()),
      fetchFilterData(sectionCode),
    ]);

    const totalItemsNumber = Number(allItemsShop?.totalItems ?? 0);
    const totalPages = Math.max(1, Math.ceil(totalItemsNumber / pageSize));
    const itemsToRender = Array.isArray(allItemsShop?.item) ? allItemsShop.item : [];
    const itemsForFilters = Array.isArray(baseItemsShop?.item) ? baseItemsShop.item : [];

    const filtersData = buildFiltersData(rawFiltersData, itemsForFilters);
    const activeFilters = buildActiveFilters(paramsMap, basePath, filtersData, rawFiltersData);
    const activeFilterCount = activeFilters.filter((filter) => filter.key !== '__reset__').length;
    const resetLink = activeFilters.find((filter) => filter.key === '__reset__')?.href ?? basePath;
    const hasActiveFilters = activeFilterCount > 0;
    const hasAvailableFilters = Array.isArray(filtersData?.filters) && filtersData.filters.length > 0;

    const renderActiveFiltersContent = () => (
      <>
        {activeFilters
          .filter((filter) => filter.key !== '__reset__')
          .map((activeFilter) => (
            <span
              key={`${activeFilter.key}:${activeFilter.value}`}
              className="inline-flex items-center gap-1 rounded-full bg-default-100 px-3 py-1 text-sm font-medium text-default-700"
            >
              <span>{activeFilter.label}</span>
              <Link
                href={activeFilter.href}
                scroll={false}
                prefetch={false}
                className="ml-1 inline-flex size-4 shrink-0 rounded-full p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-500"
              >
                <span className="sr-only">Remove filter {activeFilter.label}</span>
                <svg fill="none" stroke="currentColor" viewBox="0 0 8 8" className="size-2">
                  <path d="M1 1l6 6m0-6L1 7" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </Link>
            </span>
          ))}
        {hasActiveFilters && (
          <Link
            href={resetLink}
            scroll={false}
            prefetch={false}
            className="inline-flex items-center rounded-full border border-gray-200 bg-white py-1.5 pr-2 pl-3 text-sm font-semibold text-gray-900 hover:bg-gray-50"
          >
            Сбросить все
            <svg fill="none" stroke="currentColor" viewBox="0 0 8 8" className="ml-1 size-2">
              <path d="M1 1l6 6m0-6L1 7" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </Link>
        )}
      </>
    );

    return (
      <>
        <h1 className="text-3xl md:text-4xl font-medium mb-2 md:mb-3"></h1>
        <span className="text-xl pb-5 mb-3">
          По запросу <b>{String(searchQuery)}</b> найдено {totalItemsNumber} товаров
        </span>
        <div className="flex gap-x-6">
          {hasAvailableFilters && (
            <aside className="hidden h-full w-1/5 overflow-x-hidden md:flex">
              <div className="h-full max-h-fit w-full max-w-sm rounded-medium bg-default-50 p-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-large font-medium text-foreground">Фильтр</h2>
                  <Filter className="size-4 text-default-500" />
                </div>
                <div className="scrollbar-hide mt-4 h-max max-h-[calc(100vh-24rem)] overflow-hidden overflow-y-auto">
                  <FilterItems section={filtersData} />
                </div>
              </div>
            </aside>
          )}

          <div className="flex flex-1 flex-col">
            <div className="flex flex-col gap-3 rounded-medium bg-default-50 p-3">
              <div className="flex items-center gap-3 md:hidden">
                {hasAvailableFilters && (
                  <FilterMob
                    section={filtersData}
                    selectedCount={activeFilterCount}
                    triggerClassName="flex-1"
                  />
                )}
                <SortMenu
                  variant="flat"
                  size="sm"
                  radius="full"
                  className="w-full bg-default-100 text-default-700 md:w-auto"
                />
              </div>
              <div className="hidden items-center justify-between gap-3 md:flex">
                {hasAvailableFilters && (
                  <FilterMob
                    section={filtersData}
                    selectedCount={activeFilterCount}
                    triggerClassName="hidden md:flex"
                  />
                )}
                <SortMenu
                  variant="flat"
                  size="sm"
                  radius="full"
                  className="bg-default-100 text-default-700"
                />
              </div>

              {hasActiveFilters && (
                <div className="flex flex-wrap items-center gap-2">
                  {renderActiveFiltersContent()}
                </div>
              )}
            </div>

            <main className="mt-4 h-full w-full overflow-visible px-1">
              <div className="block">
                <div className="grid w-full gap-3 md:gap-8 grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4">
                  {itemsToRender.map((item, index) => (
                    <ProductCard
                      key={
                        (item as SearchItem)?.id ??
                        (item as SearchItem)?.code ??
                        (item as Record<string, unknown>)?.['_elasticId'] ??
                        index
                      }
                      item={item}
                      showPrices={true}
                      showDiscount={true}
                    />
                  ))}
                </div>
              </div>
              <div className="mt-5 flex w-full justify-center">
                <Pagination totalPages={totalPages} />
              </div>
            </main>
          </div>
        </div>
      </>
    );
  }

  const SearchResults = SearchResultsContent as unknown as React.FC<SearchResultsProps>;

  async function SearchPage({ searchParams }: { searchParams: AwaitableSearchParams }) {
    return (
      <div className="flex flex-col items-center justify-center mt-3 md:mt-8">
        <div className="w-full max-w-[1536px] px-2">
          <div className="w-full">
            <div className="w-full flex-1 flex-col">
              <Suspense fallback={<SearchSkeleton />}>
                <SearchResults searchParams={searchParams} />
              </Suspense>
            </div>
          </div>

          <div>
            <div></div>
            <div></div>
          </div>
        </div>
      </div>
    );
  }

  return SearchPage;
}
